export const APP_VERSION = "20260725-9"
export const MIN_TRIM_SECONDS = 0.05
export const MAX_OUTPUT_FPS = 60
export const MAX_EFFECTIVE_FPS = 100
export const MAX_MORPH_FRAMES = 10
export const PREVIEW_FRAME_CAP = 180

const byId = (id) => document.getElementById(id)

export const el = {
  formatSummary: byId("formatSummary"),
  settingsSubtitle: byId("settingsSubtitle"),
  videoInput: byId("videoInput"),
  dropzone: byId("dropzone"),
  editor: byId("editor"),
  videoPreview: byId("videoPreview"),
  previewToggle: byId("previewToggle"),
  speedReadout: byId("speedReadout"),
  speedSlider: byId("speedSlider"),
  zoomReadout: byId("zoomReadout"),
  zoomSlider: byId("zoomSlider"),
  zoomOut: byId("zoomOut"),
  zoomIn: byId("zoomIn"),
  timelineViewport: byId("timelineViewport"),
  timelineTrack: byId("timelineTrack"),
  timelineThumbnails: byId("timelineThumbnails"),
  timelineDimLeft: byId("timelineDimLeft"),
  timelineDimRight: byId("timelineDimRight"),
  timelineSelection: byId("timelineSelection"),
  startHandle: byId("startHandle"),
  endHandle: byId("endHandle"),
  seekHead: byId("seekHead"),
  trimStartInput: byId("trimStartInput"),
  trimEndInput: byId("trimEndInput"),
  trimLengthValue: byId("trimLengthValue"),
  fpsSlider: byId("fpsSlider"),
  fpsValue: byId("fpsValue"),
  fpsHelper: byId("fpsHelper"),
  resolutionSlider: byId("resolutionSlider"),
  resolutionValue: byId("resolutionValue"),
  scaleSlider: byId("scaleSlider"),
  scaleValue: byId("scaleValue"),
  smartTiming: byId("smartTiming"),
  morphSlider: byId("morphSlider"),
  morphValue: byId("morphValue"),
  morphHelper: byId("morphHelper"),
  maxFramesToggle: byId("maxFramesToggle"),
  maxFramesCard: byId("maxFramesCard"),
  maxFramesSlider: byId("maxFramesSlider"),
  maxFramesValue: byId("maxFramesValue"),
  gifQualitySlider: byId("gifQualitySlider"),
  gifQualityValue: byId("gifQualityValue"),
  colorsSlider: byId("colorsSlider"),
  colorsValue: byId("colorsValue"),
  ditherMethod: byId("ditherMethod"),
  ditherSlider: byId("ditherSlider"),
  ditherValue: byId("ditherValue"),
  ditherStrengthCard: byId("ditherStrengthCard"),
  webpQualitySlider: byId("webpQualitySlider"),
  webpQualityValue: byId("webpQualityValue"),
  webpLossless: byId("webpLossless"),
  brightnessSlider: byId("brightnessSlider"),
  brightnessValue: byId("brightnessValue"),
  saturationSlider: byId("saturationSlider"),
  saturationValue: byId("saturationValue"),
  contrastSlider: byId("contrastSlider"),
  contrastValue: byId("contrastValue"),
  grayscaleSlider: byId("grayscaleSlider"),
  grayscaleValue: byId("grayscaleValue"),
  statusText: byId("statusText"),
  workerPill: byId("workerPill"),
  livePreview: byId("livePreview"),
  resultPreview: byId("resultPreview"),
  emptyResult: byId("emptyResult"),
  previewBadge: byId("previewBadge"),
  statResolution: byId("statResolution"),
  statFramesLabel: byId("statFramesLabel"),
  statOutputFrames: byId("statOutputFrames"),
  statVideoFrames: byId("statVideoFrames"),
  statSampledFrames: byId("statSampledFrames"),
  statMorphFrames: byId("statMorphFrames"),
  statLengthLabel: byId("statLengthLabel"),
  statOutputLength: byId("statOutputLength"),
  statVideoLength: byId("statVideoLength"),
  statFpsLabel: byId("statFpsLabel"),
  statOutputFps: byId("statOutputFps"),
  statEstimatedSize: byId("statEstimatedSize"),
  progress: byId("progress"),
  progressBar: byId("progressBar"),
  errorBox: byId("errorBox"),
  convertButton: byId("convertButton"),
  cancelButton: byId("cancelButton"),
  downloadButton: byId("downloadButton"),
  performanceNote: byId("performanceNote"),
}

export const state = {
  format: "gif",
  file: null,
  sourceUrl: null,
  input: null,
  videoTrack: null,
  mediaModule: null,
  mediaReady: false,
  sourceFirstTimestamp: 0,
  sourceWidth: 0,
  sourceHeight: 0,
  sourceDuration: 0,
  sourceFps: 0,
  sourceFrameTimestamps: [],
  sourceFrameDurations: [],
  trimStart: 0,
  trimEnd: 0,
  direction: "forward",
  speed: 1,
  zoom: 1,
  previewCurrentTime: 0,
  previewPlaying: true,
  previewBuildToken: 0,
  preview: null,
  sourceCanvas: null,
  encoding: false,
  cancelRequested: false,
  resultUrl: null,
  dragRole: null,
  dragPointerId: null,
  dragMoved: false,
  dragStartX: 0,
  dragStartScroll: 0,
}

export const clamp = (value, min, max) => Math.min(max, Math.max(min, value))
export const roundTo = (value, digits = 1) => Math.round(value * 10 ** digits) / 10 ** digits

export function formatTime(seconds) {
  const safe = Math.max(0, Number(seconds) || 0)
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const wholeSeconds = Math.floor(safe % 60)
  const milliseconds = Math.floor((safe % 1) * 1000)
  const prefix = hours ? `${String(hours).padStart(2, "0")}:` : ""
  return `${prefix}${String(minutes).padStart(2, "0")}:${String(wholeSeconds).padStart(2, "0")}.${String(milliseconds).padStart(3, "0")}`
}

export function formatBytes(bytes) {
  const safe = Number(bytes)
  if (!Number.isFinite(safe) || safe <= 0) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  const exponent = Math.min(Math.floor(Math.log(safe) / Math.log(1024)), units.length - 1)
  const value = safe / 1024 ** exponent
  const digits = value >= 100 || exponent === 0 ? 0 : value >= 10 ? 1 : 2
  return `${value.toFixed(digits)} ${units[exponent]}`
}

export function formatSpeed(speed) {
  return new Map([[0.125, "⅛×"], [0.25, "¼×"], [0.5, "½×"]]).get(speed)
    || `${Number(speed).toFixed(Number.isInteger(speed) ? 0 : 1)}×`
}

export function sliderToMaxFrames(rawValue) {
  const ratio = clamp(Number(rawValue) / 1000, 0, 1)
  const exact = 10 * 200 ** ratio
  if (exact < 100) return Math.round(exact / 5) * 5
  if (exact < 500) return Math.round(exact / 10) * 10
  return Math.round(exact / 50) * 50
}

export function morphAmountToFrames(value) {
  return Math.round(clamp(Number(value), 0, 100) / 100 * MAX_MORPH_FRAMES)
}

export function getSettings() {
  return {
    format: state.format,
    fps: Number(el.fpsSlider.value),
    resolution: Number(el.resolutionSlider.value),
    scale: Number(el.scaleSlider.value),
    smartTiming: el.smartTiming.checked,
    morphAmount: Number(el.morphSlider.value),
    morphFrames: morphAmountToFrames(el.morphSlider.value),
    maxFramesEnabled: el.maxFramesToggle.checked,
    maxFrames: sliderToMaxFrames(el.maxFramesSlider.value),
    gifQuality: Number(el.gifQualitySlider.value),
    colors: Number(el.colorsSlider.value),
    ditherMethod: el.ditherMethod.value,
    ditherStrength: Number(el.ditherSlider.value),
    webpQuality: el.webpLossless.checked ? 100 : Number(el.webpQualitySlider.value),
    webpMaximumQuality: el.webpLossless.checked,
    brightness: Number(el.brightnessSlider.value),
    saturation: Number(el.saturationSlider.value),
    contrast: Number(el.contrastSlider.value),
    grayscale: Number(el.grayscaleSlider.value),
  }
}

export function getOutputDimensions(settings = getSettings()) {
  if (!state.sourceWidth || !state.sourceHeight) return { width: 0, height: 0 }
  const sourceMax = Math.max(state.sourceWidth, state.sourceHeight)
  const outputMax = Math.max(1, Math.min(sourceMax, sourceMax * settings.scale / 100, settings.resolution))
  const ratio = outputMax / sourceMax
  return {
    width: Math.max(1, Math.round(state.sourceWidth * ratio)),
    height: Math.max(1, Math.round(state.sourceHeight * ratio)),
  }
}

export function clearError() {
  el.errorBox.hidden = true
  el.errorBox.textContent = ""
}

export function showError(error) {
  const message = error instanceof Error ? error.message : String(error)
  el.errorBox.textContent = message
  el.errorBox.hidden = false
  el.statusText.textContent = message
}

export function revokeResult(showLive = true) {
  if (state.resultUrl) URL.revokeObjectURL(state.resultUrl)
  state.resultUrl = null
  el.resultPreview.removeAttribute("src")
  el.resultPreview.hidden = true
  if (showLive && state.file) {
    el.livePreview.hidden = false
    el.previewBadge.textContent = `${state.format.toUpperCase()} looping preview`
  }
  el.downloadButton.classList.add("disabled")
  el.downloadButton.removeAttribute("href")
  el.downloadButton.setAttribute("aria-disabled", "true")
}

export function disposeMedia() {
  state.previewBuildToken += 1
  state.preview?.dispose?.()
  state.preview = null
  state.input?.dispose?.()
  state.input = null
  state.videoTrack = null
  state.mediaModule = null
  state.mediaReady = false
  if (state.sourceUrl) URL.revokeObjectURL(state.sourceUrl)
  state.sourceUrl = null
  el.videoPreview.pause()
  el.videoPreview.removeAttribute("src")
  el.videoPreview.load()
  state.file = null
  state.sourceFrameTimestamps = []
  state.sourceFrameDurations = []
  state.sourceDuration = 0
  state.sourceFps = 0
  state.sourceWidth = 0
  state.sourceHeight = 0
  state.trimStart = 0
  state.trimEnd = 0
}

export function updateFormatUi() {
  const isGif = state.format === "gif"
  document.querySelectorAll("[data-format]").forEach((button) => {
    button.classList.toggle("active", button.dataset.format === state.format)
  })
  document.querySelectorAll("[data-format-only]").forEach((section) => {
    section.hidden = section.dataset.formatOnly !== state.format
  })
  el.formatSummary.textContent = isGif
    ? "GIF · indexed color · widest compatibility"
    : "Animated WebP · true color · local browser encoder"
  el.settingsSubtitle.textContent = isGif ? "GIF controls" : "Animated WebP controls"
  el.convertButton.textContent = isGif ? "Convert to GIF" : "Convert to WebP"
  el.previewBadge.textContent = isGif ? "GIF looping preview" : "WEBP looping preview"
  el.performanceNote.textContent = "Worker count adapts to CPU threads, device memory, frame size, frame count, morphing, and output format."
}

export function sanitizeFilename(filename) {
  return filename.replace(/\.[^.]+$/, "").replace(/[^a-z0-9_-]+/gi, "-").replace(/^-+|-+$/g, "") || "video"
}

export function signalPlanChange(reason = "settings") {
  dispatchEvent(new CustomEvent("converter-plan-change", { detail: { reason } }))
}
